/**
 * Fonte ÚNICA de decisão, dentro deste cliente, sobre quando um 401 significa
 * "credencial incorreta" em vez de "sessão expirada".
 *
 * O discriminador é A+B: um 401 só é lido como credencial quando (A) a URL da
 * requisição casa um item de CREDENTIAL_ENDPOINTS E (B) a mensagem do corpo é
 * exatamente INVALID_PASSWORD_MESSAGE. Qualquer outra combinação é classificada
 * como SESSÃO — inclusive 'No workspaceId!', 'No license!' e o 400 com
 * 'Authorization data not provided!' — e segue o tratamento de sessão deste cliente.
 */

/**
 * Endpoints em que a senha do usuário viaja no CORPO como credencial.
 * Um 401 vindo daqui significa "credencial incorreta", NÃO "sessão expirada".
 * Origem: backend/src/models/user.ts:75 lança AppError(401, 'invalid_password')
 * a partir de matchPassword(), chamado por changePassword() em
 * services/domain/user.domain-service.ts:329.
 * Caminho COMPLETO é obrigatório: '/password-recovery/change-password' é rota
 * pública distinta (backend/src/config/routes.ts:143) e não pode casar.
 */
export const CREDENTIAL_ENDPOINTS = ['/user/change-password'] as const;

/**
 * Mensagem exata devolvida pelo backend quando a senha atual não confere.
 * Origem: backend/src/locales/pt.json:42 ("invalid_password"), servida por
 * middleware/error.middleware.ts:70. A i18n do backend está fixada em pt_BR
 * (services/infrastructure/i18n.infra-service.ts:20 — supportedLngs: ['pt_BR']),
 * o que torna a string determinística hoje.
 * Se ela mudar, o discriminador deixa de casar e o comportamento REGRIDE ao
 * bug atual (deslogue) — nunca a uma quebra nova.
 */
export const INVALID_PASSWORD_MESSAGE = 'Senha inválida!';

/**
 * Discriminador A+B. É a ÚNICA fonte de decisão deste cliente: os dois pontos
 * do caminho da troca de senha consultam ESTE predicado — o interceptor de auth
 * (que deixa de navegar para /login) e o ramo de erro do submit do formulário de
 * senha (que exibe a mensagem no campo). São duas avaliações do MESMO predicado
 * sobre os mesmos dois campos do MESMO objeto de erro, logo não existe fonte de
 * divergência entre elas.
 *
 * Adaptação por cliente, autorizada pelo Plano de Ação e delimitada a DUAS
 * coisas:
 * (a) a metade A casa por SUFIXO do CAMINHO da requisição — descartadas a query
 *     string, o fragmento e as barras finais —, e não por igualdade exata como
 *     no mobile-client. Motivo: aqui o HttpClient recebe a URL ABSOLUTA montada
 *     em AuthService, e dois dos seis ambientes (whiteLabel e on-premise) trazem
 *     o prefixo de caminho '/whitelabel-backend' no endpoint — a igualdade exata
 *     falharia neles e o comportamento regrediria ao bug atual justamente nas
 *     builds white-label. O caminho casado continua sendo o COMPLETO, nunca
 *     parcial: o sufixo '/user/change-password' NÃO casa
 *     '/password-recovery/change-password'.
 *     O recorte do caminho é feito por operação de string, JAMAIS por `new URL`:
 *     `new URL` LANÇA quando a URL não é absoluta, e uma exceção aqui violaria a
 *     garantia das linhas 29-30 — falha do discriminador REGRIDE ao bug atual,
 *     nunca a uma quebra nova. Recortar por string não pode lançar.
 * (b) o reconhecimento NÃO é propagado por marca anexada ao objeto de erro.
 *     HttpErrorResponse é instância de classe do framework, não objeto literal
 *     como o erro do axios, e mutá-la está proibido — por isso os dois
 *     consumidores consultam o predicado em vez de ler uma marca.
 *
 * @param url - URL da requisição que falhou (`HttpErrorResponse.url`).
 * @param message - Mensagem do corpo da resposta (`HttpErrorResponse.error?.message`).
 * @returns `true` somente quando A e B casam simultaneamente.
 */
export function isCredentialError(url?: string | null, message?: string): boolean {
  if (!url || message !== INVALID_PASSWORD_MESSAGE) {
    return false;
  }

  const path = url.split('#')[0].split('?')[0].replace(/\/+$/, '');

  return CREDENTIAL_ENDPOINTS.some(endpoint => path.endsWith(endpoint));
}
